import type { SupportCenterAppointmentStatus } from '../types/supportCenterTriage'
import type {
  SupportCenterStrategicMetrics,
  SupportCenterStrategicRate,
  SupportCenterVolumeByCatalog,
} from '../types/supportCenterReports'
import {
  SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS,
  type SupportCenterAppointmentChannel,
} from '../types/supportCenterAppointments'

export type StrategicAppointmentRow = {
  id: string
  status: SupportCenterAppointmentStatus
  scheduledAt: string
  /** Instante do cancelamento, relógio de Brasília (`deleted_at`). */
  deletedAt: string | null
  patientCpf: string
  assistanceId: string
  assistanceName: string
  professionalUserId: string
  professionalName: string
  policyCode: string | null
  agreementCode: string | null
  confirmedAt: string | null
  attendanceStartedAt: string | null
  attendanceCompletedAt: string | null
  cancellationChannel: string | null
  cancelReasonName: string | null
  birthDate: string | null
  triageId: string | null
  triageStatus: string | null
  triageCreatedAt: string | null
  triagedAt: string | null
  formKey: string | null
  formSubmitted: boolean
}

export type StrategicForecastRow = {
  appointmentId: string
  patientCpf: string
  scheduledAt: string
  kind: 'data_fim' | 'atendimentos_previstos'
  endDate: string | null
  sessionsCount: number | null
}

export type BuildStrategicMetricsInput = {
  rows: StrategicAppointmentRow[]
  /** Agora em horário de Brasília, `YYYY-MM-DDTHH:mm:ss`. */
  nowWall: string
  /** Hoje em Brasília, `YYYY-MM-DD`. */
  todayYmd: string
  /**
   * Grade, horizonte e previsão ficam de fora da carga do dashboard
   * até esses grupos aparecerem na tela.
   */
  earlyStartMinutes?: number
  pastToleranceMinutes?: number
  availableSlots?: number
  occupiedSlots?: number
  nextFreeSlot?: string | null
  backlog7?: number
  backlog14?: number
  backlog30?: number
  /** `cpf|assistanceId` com horário marcado ou pré-agendado no horizonte. */
  futureOpenCaseKeys?: Set<string>
  /** CPF com horário marcado ou pré-agendado no horizonte. */
  futurePatientCpfs?: Set<string>
  forecasts?: StrategicForecastRow[]
}

const AGE_BANDS: Array<{ id: string; name: string; min: number; max: number }> = [
  { id: '0-17', name: '0–17', min: 0, max: 17 },
  { id: '18-39', name: '18–39', min: 18, max: 39 },
  { id: '40-59', name: '40–59', min: 40, max: 59 },
  { id: '60+', name: '60+', min: 60, max: 200 },
]

function emptyRate(): SupportCenterStrategicRate {
  return { rate: null, numerator: 0, denominator: 0 }
}

function makeRate(numerator: number, denominator: number): SupportCenterStrategicRate {
  if (denominator <= 0) return { rate: null, numerator, denominator: 0 }
  return {
    rate: Math.round((numerator / denominator) * 1000) / 10,
    numerator,
    denominator,
  }
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  const value =
    sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
  return Math.round(value * 10) / 10
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/** Relógio de Brasília gravado em `timestamp` sem fuso: `YYYY-MM-DDTHH:mm:ss`. */
export function wallClock(value: string | null | undefined): string {
  const raw = String(value ?? '').trim()
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/)
  return match ? `${match[1]}T${match[2]}` : ''
}

function instantMs(value: string | null | undefined): number | null {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const parsed = Date.parse(raw)
    return Number.isNaN(parsed) ? null : parsed
  }
  const wall = wallClock(raw)
  if (!wall) return null
  const parsed = Date.parse(`${wall}-03:00`)
  return Number.isNaN(parsed) ? null : parsed
}

function hoursBetween(from: string | null, to: string | null): number | null {
  const start = instantMs(from)
  const end = instantMs(to)
  if (start == null || end == null) return null
  return (end - start) / 3_600_000
}

function isPast(scheduledAt: string, nowWall: string): boolean {
  const wall = wallClock(scheduledAt)
  return Boolean(wall) && wall < nowWall
}

function caseKey(patientCpf: string, assistanceId: string): string {
  return `${onlyDigits(patientCpf)}|${assistanceId.trim()}`
}

function bump(map: Map<string, SupportCenterVolumeByCatalog>, id: string, name: string) {
  const key = id || '__none__'
  const label = name.trim() || 'Não informado'
  const existing = map.get(key)
  if (existing) {
    existing.total += 1
    return
  }
  map.set(key, { id: key, name: label, total: 1 })
}

function sortedCatalog(map: Map<string, SupportCenterVolumeByCatalog>): SupportCenterVolumeByCatalog[] {
  return [...map.values()].sort((a, b) => {
    const aNone = a.id === '__none__' || a.name === 'Não informado'
    const bNone = b.id === '__none__' || b.name === 'Não informado'
    if (aNone !== bNone) return aNone ? 1 : -1
    if (b.total !== a.total) return b.total - a.total
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

function channelLabel(value: string | null): { id: string; name: string } {
  const key = String(value ?? '').trim()
  if (
    key === 'whatsapp' ||
    key === 'telefone' ||
    key === 'videochamada' ||
    key === 'whatsapp-bot'
  ) {
    return { id: key, name: SUPPORT_CENTER_APPOINTMENT_CHANNEL_LABELS[key as SupportCenterAppointmentChannel] }
  }
  return { id: '__none__', name: 'Não informado' }
}

function ageYears(birthDate: string | null, referenceYmd: string): number | null {
  const birth = String(birthDate ?? '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birth) || !/^\d{4}-\d{2}-\d{2}$/.test(referenceYmd)) return null
  const by = Number(birth.slice(0, 4))
  const bm = Number(birth.slice(5, 7))
  const bd = Number(birth.slice(8, 10))
  const ay = Number(referenceYmd.slice(0, 4))
  const am = Number(referenceYmd.slice(5, 7))
  const ad = Number(referenceYmd.slice(8, 10))
  let age = ay - by
  if (am < bm || (am === bm && ad < bd)) age -= 1
  if (age < 0 || age > 120) return null
  return age
}

export function emptyStrategicMetrics(): SupportCenterStrategicMetrics {
  return {
    show_rate: emptyRate(),
    absenteeism_rate: emptyRate(),
    triage_conversion_rate: emptyRate(),
    appointments_without_triage: 0,
    concluded_without_future: 0,
    confirmation_rate: emptyRate(),
    pre_booking_total: 0,
    pre_booking_open: 0,
    triage_pending_count: 0,
    triage_pending_median_hours: null,
    wait_median_hours: null,
    duration_median_minutes: null,
    punctuality_rate: emptyRate(),
    open_forms: 0,
    occupancy_rate: emptyRate(),
    next_free_slot: null,
    backlog_7: 0,
    backlog_14: 0,
    backlog_30: 0,
    by_professional: [],
    reschedule_rate: emptyRate(),
    last_minute_cancel_rate: emptyRate(),
    cancel_by_reason: [],
    cancel_by_channel: [],
    unique_patients: 0,
    appointments_per_patient: null,
    return_rate: emptyRate(),
    forecast_defined: 0,
    forecast_overdue_without_return: 0,
    forecast_sessions_short: 0,
    by_assistance: [],
    by_policy: [],
    by_convenio: [],
    by_age_band: AGE_BANDS.map((band) => ({ id: band.id, name: band.name, total: 0 })).concat([
      { id: '__none__', name: 'Não informado', total: 0 },
    ]),
  }
}

export function buildStrategicMetrics(input: BuildStrategicMetricsInput): SupportCenterStrategicMetrics {
  const metrics = emptyStrategicMetrics()
  const population = input.rows.filter((row) => row.status !== 'pre_agendado')
  const nowWall = input.nowWall

  const pastOutcome = population.filter(
    (row) =>
      isPast(row.scheduledAt, nowWall) &&
      (row.status === 'realizado' || row.status === 'nao_realizado')
  )
  const realizedPast = pastOutcome.filter((row) => row.status === 'realizado').length
  const missedPast = pastOutcome.filter((row) => row.status === 'nao_realizado').length
  metrics.show_rate = makeRate(realizedPast, realizedPast + missedPast)
  metrics.absenteeism_rate = makeRate(missedPast, realizedPast + missedPast)

  const concluded = new Map<string, { caseKey: string; hasRealizado: boolean }>()
  for (const row of population) {
    if (!row.triageId || row.triageStatus !== 'concluida') continue
    const current = concluded.get(row.triageId) ?? {
      caseKey: caseKey(row.patientCpf, row.assistanceId),
      hasRealizado: false,
    }
    if (row.status === 'realizado') current.hasRealizado = true
    concluded.set(row.triageId, current)
  }
  const converted = [...concluded.values()].filter((item) => item.hasRealizado).length
  metrics.triage_conversion_rate = makeRate(converted, concluded.size)
  metrics.appointments_without_triage = population.filter(
    (row) => !row.triageId || !row.triageStatus
  ).length
  metrics.concluded_without_future = input.futureOpenCaseKeys
    ? [...concluded.values()].filter(
        (item) => item.caseKey !== '|' && !input.futureOpenCaseKeys?.has(item.caseKey)
      ).length
    : 0

  const confirmable = population.filter(
    (row) => row.status === 'marcado' || row.status === 'reagendado'
  )
  metrics.confirmation_rate = makeRate(
    confirmable.filter((row) => Boolean(row.confirmedAt)).length,
    confirmable.length
  )

  const preBookings = input.rows.filter((row) => row.status === 'pre_agendado')
  metrics.pre_booking_total = preBookings.length
  metrics.pre_booking_open = preBookings.filter((row) => !isPast(row.scheduledAt, nowWall)).length

  const pendingAges: number[] = []
  const seenPending = new Set<string>()
  for (const row of population) {
    if (row.triageStatus !== 'pendente' || !row.triageId || seenPending.has(row.triageId)) continue
    seenPending.add(row.triageId)
    const age = hoursBetween(row.triageCreatedAt, `${nowWall}-03:00`)
    if (age != null && age >= 0) pendingAges.push(age)
  }
  metrics.triage_pending_count = seenPending.size
  metrics.triage_pending_median_hours = median(pendingAges)

  const waits: number[] = []
  for (const row of population) {
    const wait = hoursBetween(row.triagedAt, row.scheduledAt)
    if (wait != null && wait >= 0) waits.push(wait)
  }
  metrics.wait_median_hours = median(waits)

  const durations: number[] = []
  for (const row of population) {
    if (row.status !== 'realizado') continue
    const hours = hoursBetween(row.attendanceStartedAt, row.attendanceCompletedAt)
    if (hours == null || hours < 0 || hours > 24) continue
    durations.push(hours * 60)
  }
  metrics.duration_median_minutes = median(durations)

  const punctualBase = population.filter(
    (row) => row.status === 'realizado' && row.attendanceStartedAt && row.scheduledAt
  )
  const punctual = punctualBase.filter((row) => {
    const deltaHours = hoursBetween(row.scheduledAt, row.attendanceStartedAt)
    if (deltaHours == null) return false
    const deltaMin = deltaHours * 60
    return (
      input.earlyStartMinutes != null &&
      input.pastToleranceMinutes != null &&
      deltaMin >= -input.earlyStartMinutes &&
      deltaMin <= input.pastToleranceMinutes
    )
  })
  metrics.punctuality_rate =
    input.earlyStartMinutes != null && input.pastToleranceMinutes != null
      ? makeRate(punctual.length, punctualBase.length)
      : emptyRate()

  metrics.open_forms = population.filter(
    (row) =>
      Boolean(row.attendanceStartedAt) &&
      Boolean(row.formKey) &&
      !row.formSubmitted &&
      row.status !== 'cancelado' &&
      row.status !== 'reagendado'
  ).length

  metrics.occupancy_rate =
    input.availableSlots != null || input.occupiedSlots != null
      ? makeRate(input.occupiedSlots ?? 0, input.availableSlots ?? 0)
      : emptyRate()
  metrics.next_free_slot = input.nextFreeSlot ?? null
  metrics.backlog_7 = input.backlog7 ?? 0
  metrics.backlog_14 = input.backlog14 ?? 0
  metrics.backlog_30 = input.backlog30 ?? 0

  const byProfessional = new Map<string, SupportCenterVolumeByCatalog>()
  for (const row of population) {
    if (row.status !== 'realizado') continue
    const id = row.professionalUserId.trim() || '__none__'
    const name = row.professionalName.trim() || (id === '__none__' ? 'Não atribuído' : 'Profissional')
    bump(byProfessional, id, name)
  }
  metrics.by_professional = sortedCatalog(byProfessional)

  const patients = new Set<string>()
  const rescheduledPatients = new Set<string>()
  const realizedByPatient = new Map<string, number>()
  for (const row of population) {
    const cpf = onlyDigits(row.patientCpf)
    if (!cpf) continue
    patients.add(cpf)
    if (row.status === 'reagendado') rescheduledPatients.add(cpf)
    if (row.status === 'realizado') {
      realizedByPatient.set(cpf, (realizedByPatient.get(cpf) ?? 0) + 1)
    }
  }
  metrics.reschedule_rate = makeRate(rescheduledPatients.size, patients.size)
  metrics.unique_patients = patients.size
  metrics.appointments_per_patient =
    patients.size > 0 ? Math.round((population.length / patients.size) * 10) / 10 : null
  const withRealized = [...realizedByPatient.values()].filter((count) => count >= 1).length
  const withReturn = [...realizedByPatient.values()].filter((count) => count >= 2).length
  metrics.return_rate = makeRate(withReturn, withRealized)

  const cancelled = population.filter((row) => row.status === 'cancelado')
  const lastMinute = cancelled.filter((row) => {
    const leadHours = hoursBetween(row.deletedAt, row.scheduledAt)
    if (leadHours == null) return false
    return leadHours >= 0 && leadHours <= 24
  })
  metrics.last_minute_cancel_rate = makeRate(lastMinute.length, cancelled.length)

  const byReason = new Map<string, SupportCenterVolumeByCatalog>()
  const byChannel = new Map<string, SupportCenterVolumeByCatalog>()
  for (const row of cancelled) {
    const reason = row.cancelReasonName?.trim() || 'Não informado'
    bump(byReason, reason, reason)
    const channel = channelLabel(row.cancellationChannel)
    bump(byChannel, channel.id, channel.name)
  }
  metrics.cancel_by_reason = sortedCatalog(byReason)
  metrics.cancel_by_channel = sortedCatalog(byChannel)

  const byAssistance = new Map<string, SupportCenterVolumeByCatalog>()
  const byPolicy = new Map<string, SupportCenterVolumeByCatalog>()
  const byConvenio = new Map<string, SupportCenterVolumeByCatalog>()
  for (const row of population) {
    const assistanceId = row.assistanceId.trim() || '__none__'
    bump(byAssistance, assistanceId, row.assistanceName.trim() || 'Não informada')
    bump(byPolicy, row.policyCode || '__none__', row.policyCode || 'Não informada')
    bump(byConvenio, row.agreementCode || '__none__', row.agreementCode || 'Não informado')
  }
  metrics.by_assistance = sortedCatalog(byAssistance)
  metrics.by_policy = sortedCatalog(byPolicy)
  metrics.by_convenio = sortedCatalog(byConvenio)

  const ageByPatient = new Map<string, { age: number | null; scheduled: string }>()
  for (const row of population) {
    const cpf = onlyDigits(row.patientCpf)
    if (!cpf) continue
    const scheduledYmd = wallClock(row.scheduledAt).slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduledYmd)) continue
    const age = ageYears(row.birthDate, scheduledYmd)
    const current = ageByPatient.get(cpf)
    if (!current || scheduledYmd >= current.scheduled) {
      ageByPatient.set(cpf, { age, scheduled: scheduledYmd })
    }
  }
  const ageCounts = new Map(metrics.by_age_band.map((band) => [band.id, 0]))
  for (const entry of ageByPatient.values()) {
    const band = AGE_BANDS.find(
      (item) => entry.age != null && entry.age >= item.min && entry.age <= item.max
    )
    const id = band?.id ?? '__none__'
    ageCounts.set(id, (ageCounts.get(id) ?? 0) + 1)
  }
  metrics.by_age_band = metrics.by_age_band.map((band) => ({
    ...band,
    total: ageCounts.get(band.id) ?? 0,
  }))

  const forecasts = input.forecasts ?? []
  metrics.forecast_defined = forecasts.length
  const seenOverduePatient = new Set<string>()
  for (const forecast of forecasts) {
    if (forecast.kind !== 'data_fim' || !forecast.endDate) continue
    if (forecast.endDate.slice(0, 10) >= input.todayYmd) continue
    const cpf = onlyDigits(forecast.patientCpf)
    if (!cpf || seenOverduePatient.has(cpf)) continue
    const hasLaterInPeriod = population.some((row) => {
      if (onlyDigits(row.patientCpf) !== cpf || row.id === forecast.appointmentId) return false
      if (row.status === 'cancelado' || row.status === 'reagendado') return false
      const scheduled = wallClock(row.scheduledAt)
      const origin = wallClock(forecast.scheduledAt)
      return Boolean(scheduled) && Boolean(origin) && scheduled > origin
    })
    if (hasLaterInPeriod || input.futurePatientCpfs?.has(cpf)) continue
    seenOverduePatient.add(cpf)
    metrics.forecast_overdue_without_return += 1
  }

  const sessionTarget = new Map<string, number>()
  for (const forecast of forecasts) {
    if (forecast.kind !== 'atendimentos_previstos' || !forecast.sessionsCount) continue
    const cpf = onlyDigits(forecast.patientCpf)
    if (!cpf) continue
    sessionTarget.set(cpf, Math.max(sessionTarget.get(cpf) ?? 0, forecast.sessionsCount))
  }
  for (const [cpf, target] of sessionTarget) {
    if ((realizedByPatient.get(cpf) ?? 0) < target) metrics.forecast_sessions_short += 1
  }

  return metrics
}
